import React, {
  FC,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { IonModal } from "@ionic/react";
import clsx from "clsx";

import { CareerChallengeSheetFormData, Topic } from "../../../state";
import { Context } from "../../../store/student";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close_no_circle.svg";

import Indicator from "./Indicator";
import Pagination from "./Pagination";
import SheetTopics from "./SheetTopics";
import SheetFutures from "./SheetFutures";
import SheetCareers from "./SheetCareers";
import SheetHopes from "./SheetHopes";
import styles from "./ChallengeSheetsModal.module.scss";

type Props = {
  calling: boolean;
  isCreateTopicsFailed: boolean;
  isOpen: boolean;
  onClose: () => void;
  createCareerChallengeSheets: () => void;
  createCareerChallengeTopics: (id: number) => void;
  saveCareerChallengeSheets: (
    id: number,
    formData: CareerChallengeSheetFormData,
  ) => void;
  submitCareerChallengeSheets: (
    id: number,
    formData: CareerChallengeSheetFormData,
  ) => void;
};

enum SheetIndex {
  Topics = 1,
  Futures = 2,
  Careers = 3,
  Hopes = 4,
}

export const MIN_SHEET_INDEX = 1;
export const MAX_SHEET_INDEX = 4;

const INITIAL_FORM_DATA: CareerChallengeSheetFormData = {
  selected_topic_ids: [],
  futures: [{ set_no: 1, when_where: "", what: "", reason: "", score: 1 }],
  careers: [{ set_no: 1, content: "", reason: "", score: 1 }],
  hopes: [{ set_no: 1, destination: "", faculty: "", reason: "", score: 1 }],
};

const ChallengeSheetsModal: FC<Props> = ({
  calling,
  isCreateTopicsFailed,
  isOpen,
  onClose,
  createCareerChallengeSheets,
  createCareerChallengeTopics,
  saveCareerChallengeSheets,
  submitCareerChallengeSheets,
}) => {
  const { contextState } = useContext(Context);
  const challengeSheet = contextState.career_challenge_sheet;
  const [formData, setFormData] =
    useState<CareerChallengeSheetFormData>(INITIAL_FORM_DATA);
  const [draftData, setDraftData] =
    useState<CareerChallengeSheetFormData>(INITIAL_FORM_DATA);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [currentSheetIndex, setCurrentSheetIndex] = useState(MIN_SHEET_INDEX);

  const renderSheetComponent = () => {
    switch (currentSheetIndex) {
      case SheetIndex.Topics:
        return (
          <SheetTopics
            calling={calling}
            topics={topics}
            isCreateTopicsFailed={isCreateTopicsFailed}
            selectedTopicIds={formData.selected_topic_ids}
            setSelectedTopicIds={(ids: number[]) =>
              setFormData(prev => ({ ...prev, selected_topic_ids: ids }))
            }
            createCareerChallengeTopics={createCareerChallengeTopics}
          />
        );
      case SheetIndex.Futures:
        return (
          <SheetFutures
            futures={formData.futures}
            setFutures={futures => setFormData(prev => ({ ...prev, futures }))}
          />
        );
      case SheetIndex.Careers:
        return (
          <SheetCareers
            careers={formData.careers}
            setCareers={careers => setFormData(prev => ({ ...prev, careers }))}
          />
        );
      case SheetIndex.Hopes:
        return (
          <SheetHopes
            hopes={formData.hopes}
            setHopes={hopes => setFormData(prev => ({ ...prev, hopes }))}
          />
        );
      default:
        return null;
    }
  };

  const isSheetValid = useMemo(() => {
    switch (currentSheetIndex) {
      case SheetIndex.Topics:
        return formData.selected_topic_ids.length > 0;
      case SheetIndex.Futures:
        return formData.futures.every(
          f => f.when_where.trim() && f.what.trim(),
        );
      case SheetIndex.Careers:
        return formData.careers.every(c => c.content.trim());
      case SheetIndex.Hopes:
        return formData.hopes.every(h => h.destination.trim());
    }
  }, [currentSheetIndex, formData]);

  const isDifferentSet = (a: number[] = [], b: number[] = []) => {
    if (a.length !== b.length) return true;
    const bs = new Set(b);
    for (const x of a) {
      if (!bs.has(x)) return true;
    }
    return false;
  };

  const canSave = useMemo(() => {
    switch (currentSheetIndex) {
      case SheetIndex.Topics:
        return isDifferentSet(
          formData.selected_topic_ids,
          draftData.selected_topic_ids,
        );
      case SheetIndex.Futures:
        return (
          JSON.stringify(formData.futures) !== JSON.stringify(draftData.futures)
        );
      case SheetIndex.Careers:
        return (
          JSON.stringify(formData.careers) !== JSON.stringify(draftData.careers)
        );
      case SheetIndex.Hopes:
        return (
          JSON.stringify(formData.hopes) !== JSON.stringify(draftData.hopes)
        );
    }
    return;
  }, [currentSheetIndex, formData, draftData]);

  const handleSave = useCallback(() => {
    if (!canSave) return;
    setDraftData(formData);
    saveCareerChallengeSheets(challengeSheet?.id ?? 0, formData);
  }, [challengeSheet?.id, formData, canSave, saveCareerChallengeSheets]);

  const initializeSheetData = () => {
    const data = {
      selected_topic_ids: INITIAL_FORM_DATA.selected_topic_ids,
      futures: INITIAL_FORM_DATA.futures,
      careers: INITIAL_FORM_DATA.careers,
      hopes: INITIAL_FORM_DATA.hopes,
    };
    setFormData(data);
    setDraftData(data);
    setTopics([]);
  };

  const handleClose = () => {
    handleSave();
    onClose();
    setCurrentSheetIndex(MIN_SHEET_INDEX);
  };

  const handleSubmit = () => {
    submitCareerChallengeSheets(challengeSheet?.id ?? 0, formData);
    onClose();
  };

  useEffect(() => {
    if (!challengeSheet?.id) return;

    const initialTopicIds = challengeSheet.topics
      .filter(t => t.selected)
      .map(t => t.id);

    const initialFutures =
      challengeSheet.futures.length > 0
        ? challengeSheet.futures
        : INITIAL_FORM_DATA.futures;

    const initialCareers =
      challengeSheet.careers.length > 0
        ? challengeSheet.careers
        : INITIAL_FORM_DATA.careers;

    const initialHopes =
      challengeSheet.hopes.length > 0
        ? challengeSheet.hopes
        : INITIAL_FORM_DATA.hopes;

    setFormData({
      selected_topic_ids: initialTopicIds,
      futures: initialFutures,
      careers: initialCareers,
      hopes: initialHopes,
    });
    setDraftData({
      selected_topic_ids: initialTopicIds,
      futures: initialFutures,
      careers: initialCareers,
      hopes: initialHopes,
    });
    setTopics(challengeSheet.topics);
  }, [challengeSheet]);

  useEffect(() => {
    const isCreatingSheet = isOpen && challengeSheet?.submitted_at !== null;

    if (isCreatingSheet) {
      initializeSheetData();
      createCareerChallengeSheets();
    }
  }, [challengeSheet?.submitted_at, isOpen, createCareerChallengeSheets]);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => handleClose()}
      className={styles.modal}
    >
      <div className={styles.wrapper}>
        <button className={styles.closeButton} onClick={() => handleClose()}>
          <CloseIcon className={styles.closeIcon} />
        </button>
        <div className={styles.header}>進路決定チャレンジシート</div>
        <div className={styles.container}>
          <div className={styles.sheet}>{renderSheetComponent()}</div>
        </div>
        <div className={styles.footer}>
          <div className={styles.indicator}>
            <Indicator index={currentSheetIndex} />
          </div>
          <div
            className={clsx(
              styles.inner,
              currentSheetIndex === MAX_SHEET_INDEX && styles.isLastSheet,
            )}
          >
            <div className={styles.buttonGroup}>
              <button
                className={styles.saveButton}
                disabled={!canSave}
                onClick={() => handleSave()}
              >
                下書き保存
              </button>
              {currentSheetIndex === MAX_SHEET_INDEX && (
                <button
                  className={styles.submitButton}
                  disabled={!isSheetValid}
                  onClick={() => handleSubmit()}
                >
                  先生に提出する
                </button>
              )}
            </div>
            <div className={styles.pagination}>
              <Pagination
                index={currentSheetIndex}
                canNext={!isSheetValid}
                onPrev={() => setCurrentSheetIndex(prev => prev - 1)}
                onNext={() => setCurrentSheetIndex(prev => prev + 1)}
                onSave={() => handleSave()}
              />
            </div>
          </div>
        </div>
      </div>
    </IonModal>
  );
};

export default ChallengeSheetsModal;
